/**
 * Global toast utility for imperative toast calls
 * This provides a drop-in replacement for react-toastify's toast API
 */

import type { ToastAction, ToastType } from './Toast';

interface ToastOptions {
  description?: string;
  duration?: number;
  /** Botón inline (ej. "Deshacer"). Al ejecutarlo el toast se cierra. */
  action?: ToastAction;
}

type ToastHandler = (
  type: ToastType,
  message: string,
  description?: string,
  duration?: number,
  action?: ToastAction
) => string;
type DismissHandler = (id: string) => void;
type DismissAllHandler = () => void;
type UpdateHandler = (
  id: string,
  options: { message?: string; description?: string; type?: ToastType }
) => void;

let toastHandler: ToastHandler | null = null;
let dismissHandler: DismissHandler | null = null;
let dismissAllHandler: DismissAllHandler | null = null;
let updateHandler: UpdateHandler | null = null;

/**
 * Registers the toast handlers from the ToastProvider
 * This is called internally by ToastProvider
 */
export const registerToastHandlers = (
  addToast: ToastHandler,
  dismiss: DismissHandler,
  dismissAll: DismissAllHandler,
  update: UpdateHandler
) => {
  toastHandler = addToast;
  dismissHandler = dismiss;
  dismissAllHandler = dismissAll;
  updateHandler = update;
};

/**
 * Unregisters toast handlers (useful for cleanup)
 */
export const unregisterToastHandlers = () => {
  toastHandler = null;
  dismissHandler = null;
  dismissAllHandler = null;
  updateHandler = null;
};

const createToast = (type: ToastType, message: string, options?: ToastOptions | string): string => {
  if (!toastHandler) {
    // eslint-disable-next-line no-console
    console.warn('Toast: ToastProvider not mounted. Toast will not be displayed.');
    return '';
  }

  // Support for legacy string-only second argument
  const description = typeof options === 'string' ? undefined : options?.description;
  const duration = typeof options === 'string' ? undefined : options?.duration;
  const action = typeof options === 'string' ? undefined : options?.action;

  return toastHandler(type, message, description, duration, action);
};

/**
 * Global toast object - drop-in replacement for react-toastify
 *
 * Usage:
 * - toast.success('Message')
 * - toast.success('Message', { description: 'Details', duration: 3000 })
 * - toast.error('Error message')
 * - toast.warning('Warning message')
 * - toast.info('Info message')
 * - toast.loading('Loading...')
 * - toast.dismiss(id)
 * - toast.dismissAll()
 */
export const toast = {
  success: (message: string, options?: ToastOptions | string) => createToast('success', message, options),
  error: (message: string, options?: ToastOptions | string) => createToast('error', message, options),
  warning: (message: string, options?: ToastOptions | string) => createToast('warning', message, options),
  info: (message: string, options?: ToastOptions | string) => createToast('info', message, options),
  loading: (message: string, options?: ToastOptions | string) => createToast('loading', message, options),

  dismiss: (id: string) => {
    if (dismissHandler) {
      dismissHandler(id);
    }
  },

  dismissAll: () => {
    if (dismissAllHandler) {
      dismissAllHandler();
    }
  },

  update: (id: string, options: { message?: string; description?: string; type?: ToastType }) => {
    if (updateHandler) {
      updateHandler(id, options);
    }
  },

  /**
   * Promise-based toast helper
   * Shows loading toast, then success/error based on promise result
   */
  promise: async <T>(
    promise: Promise<T>,
    options: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((err: unknown) => string);
    }
  ): Promise<T> => {
    const id = toast.loading(options.loading);

    try {
      const result = await promise;
      toast.dismiss(id);
      const successMessage =
        typeof options.success === 'function' ? options.success(result) : options.success;
      toast.success(successMessage);
      return result;
    } catch (err) {
      toast.dismiss(id);
      const errorMessage = typeof options.error === 'function' ? options.error(err) : options.error;
      toast.error(errorMessage);
      throw err;
    }
  }
};

export default toast;
